/**
 * Data layer for `real-time-conversion-fraud-detection`.
 *
 * Typed fetch wrapper over /api/v1/real-time-conversion-fraud-detection.
 * Raw `fetch()` is confined to this file per the dash contract.
 *
 * Live pulse page polls list() every 5s via TanStack Query. Each row is
 * one recent conversion with a fraud verdict emitted by the backend rule
 * engine. The shape is intentionally flexible: tiles are derived from the
 * rows themselves so the UI survives schema drift on the BE side.
 */

export type FraudStatus = "flagged" | "blocked" | "review" | "clean";

export type FraudReason =
	| "dup_txn"
	| "geo_spoof"
	| "rapid_cvr"
	| "ip_sharing"
	| "ua_anomaly"
	| "chargeback_risk"
	| "unknown";

export interface RealTimeConversionFraudDetectionRow {
	id: string;
	tenant_id: string;
	created_at: number;
	updated_at: number;
	/** Conversion id surfaced to operators. */
	conversion_id?: string;
	/** Advertiser slug / display name. */
	advertiser?: string;
	/** Fraud score 0-100; ≥60 = red. */
	score?: number;
	/** Verdict bucket. */
	status?: FraudStatus;
	/** Primary reason chip. */
	reason?: FraudReason;
	/** Monetary amount that would have been paid (USD cents). */
	amount_cents?: number;
	[key: string]: unknown;
}

export interface ListRealTimeConversionFraudDetectionResponse {
	count: number;
	items: RealTimeConversionFraudDetectionRow[];
	/** Optional server-derived aggregates; FE falls back to local calc. */
	summary?: {
		flagged_last_min?: number;
		duplicate_rate?: number;
		chargeback_risk?: number;
		blocked_revenue_cents?: number;
	};
}

const BASE = "/api/v1/real-time-conversion-fraud-detection";

async function parseOrThrow<T>(res: Response): Promise<T> {
	const text = await res.text();
	let data: unknown = null;
	if (text) {
		try {
			data = JSON.parse(text);
		} catch {
			data = text;
		}
	}
	if (!res.ok) {
		const msg =
			data &&
			typeof data === "object" &&
			data !== null &&
			"error" in data &&
			typeof (data as { error: unknown }).error === "string"
				? (data as { error: string }).error
				: `HTTP ${res.status}`;
		throw new Error(msg);
	}
	return data as T;
}

export async function list(): Promise<ListRealTimeConversionFraudDetectionResponse> {
	const res = await fetch(BASE, {
		method: "GET",
		credentials: "include",
		headers: {
			accept: "application/json",
			"x-tenant-id": "t1",
		},
	});
	return parseOrThrow<ListRealTimeConversionFraudDetectionResponse>(res);
}

export const realTimeConversionFraudDetectionQueryKeys = {
	all: ["real-time-conversion-fraud-detection"] as const,
	list: () => [...realTimeConversionFraudDetectionQueryKeys.all] as const,
	detail: (id: string) =>
		[...realTimeConversionFraudDetectionQueryKeys.all, id] as const,
};

// ---------------------------------------------------------------------------
// Pure helpers — used by the page + hooks; kept in api.ts so the feature
// dir stays 5 files (contract cap is ≥1 .tsx; no extra util file needed).
// ---------------------------------------------------------------------------

export const REASON_LABEL: Record<FraudReason, string> = {
	dup_txn: "Duplicate txn",
	geo_spoof: "Geo spoof",
	rapid_cvr: "Rapid CVR",
	ip_sharing: "IP sharing",
	ua_anomaly: "UA anomaly",
	chargeback_risk: "Chargeback risk",
	unknown: "Unknown",
};

export const STATUS_LABEL: Record<FraudStatus, string> = {
	flagged: "Flagged",
	blocked: "Blocked",
	review: "Review",
	clean: "Clean",
};

export function isFraudulent(row: RealTimeConversionFraudDetectionRow): boolean {
	if (row.status === "clean") return false;
	if (typeof row.score === "number" && row.score >= 60) return true;
	return row.status === "flagged" || row.status === "blocked";
}

export function scoreBucket(
	score: number | undefined,
): "high" | "mid" | "low" {
	if (typeof score !== "number") return "low";
	if (score >= 60) return "high";
	if (score >= 30) return "mid";
	return "low";
}

export function formatUsd(cents: number | undefined): string {
	if (typeof cents !== "number" || !Number.isFinite(cents)) return "$0";
	const dollars = cents / 100;
	return dollars.toLocaleString("en-US", {
		style: "currency",
		currency: "USD",
		maximumFractionDigits: 0,
	});
}

export function formatTs(seconds: number | undefined): string {
	if (typeof seconds !== "number" || !Number.isFinite(seconds)) return "—";
	const d = new Date(seconds * 1000);
	return d.toLocaleTimeString("en-US", {
		hour12: false,
		hour: "2-digit",
		minute: "2-digit",
		second: "2-digit",
	});
}

export interface DerivedTiles {
	flagged_last_min: number;
	duplicate_rate: number;
	chargeback_risk: number;
	blocked_revenue_cents: number;
}

export function deriveTiles(
	rows: RealTimeConversionFraudDetectionRow[],
	summary?: ListRealTimeConversionFraudDetectionResponse["summary"],
): DerivedTiles {
	if (summary) {
		return {
			flagged_last_min: summary.flagged_last_min ?? 0,
			duplicate_rate: summary.duplicate_rate ?? 0,
			chargeback_risk: summary.chargeback_risk ?? 0,
			blocked_revenue_cents: summary.blocked_revenue_cents ?? 0,
		};
	}
	const nowSec = Math.floor(Date.now() / 1000);
	const last60 = rows.filter(
		(r) => typeof r.created_at === "number" && nowSec - r.created_at <= 60,
	);
	const flaggedLast = last60.filter(isFraudulent).length;
	const dupCount = rows.filter((r) => r.reason === "dup_txn").length;
	const duplicateRate = rows.length > 0 ? dupCount / rows.length : 0;
	const cbCount = rows.filter((r) => r.reason === "chargeback_risk").length;
	const chargebackRisk = rows.length > 0 ? cbCount / rows.length : 0;
	const blockedRevenue = rows
		.filter((r) => r.status === "blocked")
		.reduce((acc, r) => acc + (typeof r.amount_cents === "number" ? r.amount_cents : 0), 0);
	return {
		flagged_last_min: flaggedLast,
		duplicate_rate: duplicateRate,
		chargeback_risk: chargebackRisk,
		blocked_revenue_cents: blockedRevenue,
	};
}
